import { useCallback, useRef, useState, type DragEvent } from "react";

/**
 * Drag-and-drop onto any existing upload target.
 *
 * Every upload surface in the app already has a working "pick one file"
 * flow built around a hidden `<input type="file">` and a callback that takes
 * a single `File` — this hook doesn't replace any of that, it just gives a
 * container the drag events needed to call that same callback once per
 * dropped file, plus a boolean for a "drop it here" highlight while the
 * drag is over it.
 *
 * `dragCounter` rather than a plain boolean for `isOver`: a dragenter/
 * dragleave pair fires for every descendant element the pointer crosses,
 * not just the container's own boundary, so leaving a child and re-entering
 * the parent would otherwise flicker the highlight off between the two
 * events. Counting enter/leave pairs and only clearing on zero keeps it
 * lit for the whole time the drag is anywhere inside the container.
 */
export function useFileDrop(onDropFiles: (files: File[]) => void) {
  const [isOver, setIsOver] = useState(false);
  const dragCounter = useRef(0);

  const onDragEnter = useCallback((event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragCounter.current += 1;
    setIsOver(true);
  }, []);

  const onDragOver = useCallback((event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
  }, []);

  const onDragLeave = useCallback((event: DragEvent) => {
    if (!event.dataTransfer.types.includes("Files")) return;
    event.preventDefault();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsOver(false);
  }, []);

  const onDrop = useCallback(
    (event: DragEvent) => {
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      dragCounter.current = 0;
      setIsOver(false);
      const files = Array.from(event.dataTransfer.files);
      if (files.length) onDropFiles(files);
    },
    [onDropFiles],
  );

  return { isOver, dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
